/**
 * Scheme constants for "Mitigation of Waterlogging in District Rahim Yar Khan (PIAIP)".
 *
 * Every figure here is carried over verbatim from the approved project tracker as
 * captured in the Claude Design prototype. Changing any of them changes reported
 * progress, so they are kept in one place and covered by tests.
 */
export const SCHEME_START = '2026-04-09';
export const SCHEME_END = '2028-12-31';
/** Sanctioned cost of the scheme, Rs million. */
export const BUDGET = 2859.433;
/** Budget of the non-construction heads (land, environment, contingencies, tax), Rs million. */
export const MISC_BUDGET = 589.381;
export const CATS = [
    { k: 'civil', n: 'Civil Work', w: 0.516, budget: 1280.311 },
    { k: 'elec', n: 'Electrical Work', w: 0.255, budget: 531.984 },
    { k: 'mech', n: 'Mechanical Work', w: 0.229, budget: 457.758 },
];
/**
 * Sanctioned cost of the construction heads alone (civil, electrical, mechanical),
 * Rs million. Financial progress is measured against this, so that it is compared
 * like for like with physical progress — land, environment, contingencies and tax
 * produce no physical work and would otherwise make spending look ahead of work.
 */
export const WORKS_BUDGET = CATS.reduce((a, c) => a + c.budget, 0);
/** Drains to be cut new. */
export const NEWD = [
    { k: 'd1m', n: 'D1 Main Drain', ft: 38250 },
    { k: 'd1s', n: 'D1 Sub Drain', ft: 13031 },
    { k: 'd2', n: 'D2 Drain', ft: 12615 },
    { k: 'd3', n: 'D3 Drain', ft: 12524 },
    { k: 'l1d1', n: '1L of D1 Main', ft: 2697 },
    { k: 'l1jar', n: '1L Jarewala', ft: 16500 },
    { k: 'link', n: 'Link Channels (6 Nos.)', ft: 12017 },
];
/** Existing drains to be rehabilitated. */
export const REHAB = [
    { k: 'm2l', n: '2L Manthar Drain', ft: 31568 },
    { k: 'm3l', n: '3L Manthar Drain', ft: 18007 },
    { k: 'm4l', n: '4L Manthar Drain', ft: 17700 },
    { k: 'pattan', n: 'Pattan Minara Main Drain', ft: 30000 },
    { k: 'manthar', n: 'Manthar Main Drain', ft: 138579 },
    { k: 'khanpur', n: 'Khanpur Main Drain', ft: 84000 },
    { k: 'jarewala', n: 'Jarewala Main Drain', ft: 25446 },
];
export const UNITS = [
    {
        k: 'quarters',
        n: 'Tubewell Operator Quarters',
        total: 91,
        cat: 'civil',
        unit: 'quarters',
        stages: [
            ['Excavation', 10],
            ['Foundation / Plinth', 20],
            ['Superstructure', 50],
            ['Roof Slab', 70],
            ['Finishing', 95],
            ['Complete / Handed Over', 100],
        ],
    },
    {
        k: 'pumpHouses',
        n: 'Pump Houses',
        total: 37,
        cat: 'civil',
        unit: 'pump houses',
        stages: [
            ['Foundation / Plinth', 10],
            ['DPC', 20],
            ['Superstructure', 50],
            ['Roof Slab', 70],
            ['Finishing', 95],
            ['Complete / Handed Over', 100],
        ],
    },
    {
        k: 'pumpStations',
        n: 'Pumping Stations',
        total: 5,
        cat: 'civil',
        unit: 'pumping stations',
        stages: [
            ['Excavation', 10],
            ['Foundation / Plinth', 20],
            ['DPC', 30],
            ['Superstructure', 55],
            ['Roof Slab', 72],
            ['Finishing', 95],
            ['Complete / Handed Over', 100],
        ],
    },
    {
        k: 'solarTW',
        n: 'Solarization of Tubewells (New & Existing)',
        total: 87,
        cat: 'elec',
        unit: 'tubewells',
        stages: [
            ['Procurement of PV Panels', 15],
            ['Frame Installation', 35],
            ['PV Panels Installation', 65],
            ['Wiring', 80],
            ['VFD / Inverter', 100],
        ],
    },
    {
        k: 'elecTW',
        n: 'Electrification of Tubewells',
        total: 37,
        cat: 'elec',
        unit: 'tubewells',
        stages: [
            ['Installation of Poles', 15],
            ['HT Lines', 35],
            ['Transformers', 65],
            ['Wiring', 80],
            ['Panel Boxes', 100],
        ],
    },
    {
        k: 'solarPS',
        n: 'Solarization of Pumping Stations',
        total: 5,
        cat: 'elec',
        unit: 'stations',
        stages: [
            ['Procurement of PV Panels', 15],
            ['Frame Installation', 35],
            ['PV Panels Installation', 65],
            ['Wiring', 80],
            ['VFD / Inverter', 100],
        ],
    },
    {
        k: 'elecPS',
        n: 'Electrification of Pumping Stations',
        total: 5,
        cat: 'elec',
        unit: 'stations',
        stages: [
            ['Installation of Poles', 15],
            ['HT Lines', 35],
            ['Transformers', 65],
            ['Wiring', 80],
            ['Panel Boxes', 100],
        ],
    },
    {
        k: 'mechTW',
        n: 'Installation of Tubewells',
        total: 37,
        cat: 'mech',
        unit: 'tubewells',
        stages: [
            ['Boring', 25],
            ['Lowering of Pumping Assembly', 55],
            ['Installation of Motors', 80],
            ['Laying of Delivery Line', 100],
        ],
    },
    {
        k: 'mechPS',
        n: 'Installation of Pumps at Pumping Stations',
        total: 5,
        cat: 'mech',
        unit: 'stations',
        stages: [
            ['Procurement of Pumps', 25],
            ['Lowering of Pumping Assembly', 55],
            ['Installation of Motors', 80],
            ['Laying of Delivery Pipes', 100],
        ],
    },
];
/** Material mobilisation columns: [key, label]. */
export const MATCOLS = [
    ['cement', 'Cement'],
    ['sand', 'Sand'],
    ['crush', 'Crush'],
    ['steel', 'Steel'],
    ['shuttering', 'Shuttering'],
];
export const MISC_MILESTONES = [
    { k: 'land', n: 'Land Acquisition', w: 71.5 },
    { k: 'env', n: 'Environmental Monitoring', w: 1.5 },
    { k: 'cont', n: 'Physical Contingencies', w: 7.7 },
    { k: 'tax', n: 'Punjab Sales Tax', w: 19.3 },
];
export const ROLES = [
    {
        k: 'admin',
        label: 'System Administrator',
        desc: 'Creates and removes accounts, assigns roles and resets passwords. Does not enter or approve progress.',
        can: { users: 1 },
    },
    {
        k: 'pmu',
        label: 'PMU In-charge',
        desc: 'Enters weekly progress, approves returns and records contractor payments.',
        can: { entry: 1, approve: 1, payments: 1, issues: 1, packages: 1 },
    },
    {
        k: 'field',
        label: 'Field Officer',
        desc: 'Records weekly site progress and submits it for approval.',
        can: { entry: 1, issues: 1 },
    },
    {
        k: 'sdo',
        label: 'Sub-Divisional Officer',
        desc: 'Records weekly progress, maintains the issues register and contract packages.',
        can: { entry: 1, issues: 1, packages: 1 },
    },
    {
        k: 'accounts',
        label: 'Accounts Officer',
        desc: 'Records payments released to contractors. No access to physical progress entry.',
        can: { payments: 1 },
    },
    {
        k: 'executive',
        label: 'Senior Executive',
        desc: 'Sees the executive overview and the printable reports — a clear, read-only picture of where the scheme stands. No data entry, approvals or registers.',
        can: {},
    },
    {
        k: 'viewer',
        label: 'Observer',
        desc: 'Reads the dashboard and reports only. Cannot change anything.',
        can: {},
    },
];
export function roleDef(k) {
    return ROLES.find((r) => r.k === k) ?? ROLES[ROLES.length - 1];
}
/** Whether a role holds a capability. */
export function roleCan(role, cap) {
    return !!roleDef(role).can[cap];
}
export const ROLE_KEYS = ROLES.map((r) => r.k);
export function isRoleKey(v) {
    return typeof v === 'string' && ROLE_KEYS.includes(v);
}
// -------------------------------------------------------------- lookups
export const CAT_NAMES = {
    civil: 'Civil',
    elec: 'Electrical',
    mech: 'Mechanical',
    misc: 'Miscellaneous',
};
export function catName(k) {
    return CAT_NAMES[k ?? ''] ?? 'Misc';
}
/** Every progress-bearing component of the scheme, drains first then unit groups. */
export function componentList() {
    const list = [
        { k: 'newDrains', n: 'Construction of New Drains', cat: 'civil' },
        { k: 'rehabDrains', n: 'Rehabilitation of Existing Drains', cat: 'civil' },
    ];
    UNITS.forEach((g) => list.push({ k: g.k, n: g.n, cat: g.cat, group: g }));
    return list;
}
export function unitGroup(k) {
    return UNITS.find((g) => g.k === k);
}
export function drainList(kind) {
    return kind === 'drainsNew' ? NEWD : REHAB;
}
//# sourceMappingURL=domain.js.map